/**
 * FastAPI error bodies put the payload on `detail`. Orval only types the
 * request-validation list. Grower, site, plot, and row checks also return a
 * string, or an object with `message` and the records that failed.
 */

const REQUEST_LOCATION_ROOTS = new Set([
  "body",
  "cookie",
  "header",
  "path",
  "query",
]);

const PYDANTIC_VALUE_ERROR_PREFIX = "Value error, ";

export const API_ERROR_MESSAGE_MAX_LENGTH = 280;

const truncateMessage = (message: string): string =>
  message.length <= API_ERROR_MESSAGE_MAX_LENGTH
    ? message
    : `${message.slice(0, API_ERROR_MESSAGE_MAX_LENGTH - 3)}...`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

const readNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const humanizeFieldName = (fieldName: string): string => {
  const words = fieldName.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatRequestErrorLocation = (loc: unknown): string | null => {
  if (!Array.isArray(loc)) {
    return null;
  }

  const parts = loc.flatMap((segment) => {
    if (typeof segment === "number" && Number.isFinite(segment)) {
      return [`item ${segment + 1}`];
    }
    if (typeof segment === "string" && !REQUEST_LOCATION_ROOTS.has(segment)) {
      return [humanizeFieldName(segment)];
    }
    return [];
  });

  return parts.length > 0 ? parts.join(" / ") : null;
};

const cleanValidationMessage = (message: string): string =>
  message.startsWith(PYDANTIC_VALUE_ERROR_PREFIX)
    ? message.slice(PYDANTIC_VALUE_ERROR_PREFIX.length)
    : message;

/** Request-body validation: `detail` is a list of `{ loc, msg, type }`. */
export const formatRequestValidationDetail = (
  detail: unknown,
): string | null => {
  if (!Array.isArray(detail)) {
    return null;
  }

  const messages = detail.flatMap((item) => {
    if (!isRecord(item)) {
      return [];
    }
    const message = readString(item.msg);
    if (message == null) {
      return [];
    }
    const cleaned = cleanValidationMessage(message);
    const location = formatRequestErrorLocation(item.loc);
    return [location == null ? cleaned : `${location}: ${cleaned}`];
  });

  return messages.length > 0 ? truncateMessage(messages.join("; ")) : null;
};

const formatLabelOrId = (
  item: Record<string, unknown>,
  labelKey: string,
  idKey: string,
  noun: string,
): string | null => {
  const label = readString(item[labelKey]);
  if (label != null) {
    return label;
  }
  const id = readNumber(item[idKey]);
  return id == null ? null : `${noun} ${id}`;
};

const formatInvalidItem = (item: unknown): string | null => {
  if (!isRecord(item)) {
    return null;
  }

  const rowNumber = readNumber(item.row_number);
  const index = readNumber(item.index);
  const label =
    readString(item.grower_label) ??
    readString(item.site_label) ??
    readString(item.plot_label) ??
    (rowNumber == null ? null : `row ${rowNumber}`) ??
    (index == null ? null : `item ${index + 1}`);
  const plotId = readNumber(item.plot_id);
  const named =
    label == null
      ? null
      : plotId == null
        ? label
        : `${label} in plot ${plotId}`;
  const reason = readString(item.reason);
  if (named == null) {
    return reason;
  }
  return reason == null ? named : `${named} (${reason})`;
};

const formatOverlapItem = (item: unknown): string | null => {
  if (!isRecord(item)) {
    return null;
  }

  const rowNumberA = readNumber(item.row_number_a);
  const rowNumberB = readNumber(item.row_number_b);
  if (rowNumberA != null && rowNumberB != null) {
    const plotId = readNumber(item.plot_id);
    const rows = `rows ${rowNumberA} and ${rowNumberB}`;
    return plotId == null ? rows : `${rows} in plot ${plotId}`;
  }

  const indexA = readNumber(item.index_a);
  const indexB = readNumber(item.index_b);
  if (indexA != null && indexB != null) {
    const labelA = readString(item.plot_label_a) ?? `item ${indexA + 1}`;
    const labelB = readString(item.plot_label_b) ?? `item ${indexB + 1}`;
    return `${labelA} and ${labelB}`;
  }

  const rowNumber = readNumber(item.row_number);
  if (rowNumber != null && readNumber(item.row_id) != null) {
    const plotId = readNumber(item.plot_id);
    const row = `row ${rowNumber}`;
    return plotId == null ? row : `${row} in plot ${plotId}`;
  }

  return (
    formatLabelOrId(item, "grower_label", "grower_id", "grower") ??
    formatLabelOrId(item, "site_label", "site_id", "site") ??
    formatLabelOrId(item, "plot_label", "plot_id", "plot")
  );
};

const formatPlotItem = (item: unknown): string | null => {
  if (!isRecord(item)) {
    return null;
  }
  const label = readString(item.plot_label);
  if (label != null) {
    return label;
  }
  const plotId = readNumber(item.plot_id);
  if (plotId != null) {
    return `plot ${plotId}`;
  }
  const index = readNumber(item.index);
  return index == null ? null : `item ${index + 1}`;
};

const formatTrialItem = (item: unknown): string | null => {
  if (!isRecord(item)) {
    return null;
  }
  const trialId = readNumber(item.trial_id);
  const name =
    readString(item.trial_name) ??
    (trialId == null ? null : `trial ${trialId}`);
  if (name == null) {
    return null;
  }
  const year = readNumber(item.year);
  return year == null ? name : `${name} (${year})`;
};

const formatVarietyItem = (item: unknown): string | null => {
  if (!isRecord(item)) {
    return null;
  }
  const varietyId = readNumber(item.variety_id);
  return varietyId == null ? null : `variety ${varietyId}`;
};

const formatLayoutErrorItem = (item: unknown): string | null => {
  if (!isRecord(item)) {
    return null;
  }
  const message = readString(item.message);
  if (message == null) {
    return null;
  }
  const plotId = readNumber(item.plot_id);
  const rowIds = Array.isArray(item.row_ids)
    ? item.row_ids.flatMap((rowId) => {
        const value = readNumber(rowId);
        return value == null ? [] : [String(value)];
      })
    : [];
  const details = [
    plotId == null ? null : `plot ${plotId}`,
    rowIds.length > 0 ? `rows ${rowIds.join(", ")}` : null,
  ].flatMap((part) => (part == null ? [] : [part]));
  return details.length === 0 ? message : `${message} (${details.join(", ")})`;
};

const joinItems = (
  items: unknown,
  formatItem: (item: unknown) => string | null,
): string | null => {
  if (!Array.isArray(items)) {
    return null;
  }
  const formatted = items.flatMap((item) => {
    const text = formatItem(item);
    return text == null ? [] : [text];
  });
  return formatted.length > 0 ? formatted.join(", ") : null;
};

const formatIdList = (
  ids: unknown,
  singular: string,
  plural: string,
): string | null => {
  if (!Array.isArray(ids)) {
    return null;
  }
  const numbers = ids.flatMap((id) => {
    const value = readNumber(id);
    return value == null ? [] : [String(value)];
  });
  if (numbers.length === 0) {
    return null;
  }
  const noun = numbers.length === 1 ? singular : plural;
  return `${noun} ${numbers.join(", ")}`;
};

/** Domain validation: `detail` is `{ message, ...failing records }`. */
export const formatDomainValidationDetail = (
  detail: unknown,
): string | null => {
  if (!isRecord(detail)) {
    return null;
  }

  const specifics = [
    joinItems(detail.invalid, formatInvalidItem),
    joinItems(detail.overlaps, formatOverlapItem),
    joinItems(detail.sites, (item) =>
      isRecord(item)
        ? formatLabelOrId(item, "site_label", "site_id", "site")
        : null,
    ),
    joinItems(detail.plots, formatPlotItem),
    joinItems(detail.trials, formatTrialItem),
    joinItems(detail.varieties, formatVarietyItem),
    joinItems(detail.errors, formatLayoutErrorItem),
    formatIdList(detail.site_ids, "site", "sites"),
    formatIdList(detail.plot_ids, "plot", "plots"),
    formatIdList(detail.row_ids, "row", "rows"),
  ].flatMap((part) => (part == null ? [] : [part]));

  const message = readString(detail.message);
  if (message == null) {
    return specifics.length > 0 ? truncateMessage(specifics.join("; ")) : null;
  }
  return truncateMessage(
    specifics.length > 0 ? `${message}: ${specifics.join("; ")}` : message,
  );
};

/** User-facing text for a FastAPI error body. `fallback` is used when `detail` is missing or unrecognized. */
export const getApiErrorMessage =
  (fallback: string) =>
  (data: unknown): string => {
    if (!isRecord(data) || !("detail" in data)) {
      return fallback;
    }

    const stringDetail = readString(data.detail);
    if (stringDetail != null) {
      return truncateMessage(stringDetail);
    }

    return (
      formatRequestValidationDetail(data.detail) ??
      formatDomainValidationDetail(data.detail) ??
      fallback
    );
  };
